import { useContext, useMemo } from 'react'

import GeoContext from 'contexts/geoContext'
import { sortBy, uniqueBy } from 'lib/array/arrayUtils'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import findClosestAirport from 'lib/flights/findClosestAirport'
import { useAppSelector } from 'hooks/reduxHooks'
import { getAirportsByCode, getDefaultAirport } from 'selectors/flightsSelectors'
import { useLocalizedPlaces } from 'hooks/usePlace'
import { getFlightDealOriginCityPlaceId } from 'home/constants/flightDealOriginCityPlaceIds'

type FlightLocationDetails = [
  locations: Array<App.Airport>,
  defaultLocation: App.Airport | undefined,
]

/**
 * Origin airports offered by the given deals, plus the one closest to the user
 */
export function useFlightDealOriginLocations(flightDeals: Array<Pick<App.FlightDeal, 'originAirportCode'>>): FlightLocationDetails {
  const { latitude, longitude } = useContext(GeoContext)
  const airportsByCode = useAppSelector(getAirportsByCode)
  const defaultAirport = useAppSelector(getDefaultAirport)

  const uniqueOriginDeals = useMemo(
    () => uniqueBy(flightDeals, deal => deal.originAirportCode).map(deal => ({
      deal,
      cityPlaceId: getFlightDealOriginCityPlaceId(deal.originAirportCode),
    })),
    [flightDeals],
  )

  const originCityPlaceIds = useMemo(
    () => Array.from(new Set(
      uniqueOriginDeals
        .map(({ cityPlaceId }) => cityPlaceId)
        .filter((id): id is string => !!id),
    )),
    [uniqueOriginDeals],
  )

  const localizedOriginCities = useLocalizedPlaces(originCityPlaceIds)

  const locations = useMemo(() => {
    const uniqueLocations = uniqueOriginDeals
      .map(({ deal, cityPlaceId }): App.Airport => {
        const airport = airportsByCode[deal.originAirportCode]
        const resolvedCityName = cityPlaceId ? localizedOriginCities[cityPlaceId]?.place?.name : undefined

        return {
          ...airport,
          name: resolvedCityName ?? airport?.name ?? deal.originAirportCode,
        }
      })
      .filter((airport): airport is App.Airport => !!airport?.code)

    return sortBy(uniqueLocations, airport => airport.name, 'asc')
  }, [airportsByCode, uniqueOriginDeals, localizedOriginCities])

  const defaultLocation = useMemo<App.Airport | undefined>(() => {
    const lat = latitude ?? defaultAirport?.latitude
    const long = longitude ?? defaultAirport?.longitude

    return findClosestAirport(locations, lat, long)
  },
  [latitude, longitude, locations, defaultAirport])

  return [locations, defaultLocation ?? locations[0]]
}

function useFlightDealLocations(category?: string): FlightLocationDetails {
  const [flightDeals] = useFlightDeals(category)
  return useFlightDealOriginLocations(flightDeals)
}

export default useFlightDealLocations
